import { unstable_rethrow } from 'next/navigation';
import { useCallback } from 'react';

import { RecaptchaV3Action } from './RecaptchaV3Action';
import { useRecaptchaV3Callback } from './useRecaptchaV3Callback';

/**
 * Callback invoked when Google Sign-In returns a credential. Defined locally so the
 * package does not depend on app code; structurally identical to the app's
 * google-login `GoogleLoginSuccessCallback`.
 */
export type GoogleLoginSuccessCallback = (response: google.accounts.id.CredentialResponse) => void;

export interface GoogleCallbackConfig {
    action: RecaptchaV3Action.LOGIN_WITH_GOOGLE | RecaptchaV3Action.SIGN_UP_WITH_GOOGLE;
    onSuccess: (googleCredential: string, recaptchaToken: string | undefined) => Promise<void>;
    onError: (error: Error) => void;
}

export function useGoogleV3Callback({ action, onSuccess, onError }: GoogleCallbackConfig): GoogleLoginSuccessCallback {
    const recaptchaCallback = useRecaptchaV3Callback(action);

    return useCallback<GoogleLoginSuccessCallback>(
        (response) => {
            const { credential } = response;

            recaptchaCallback()
                .then((recaptchaToken) => {
                    return onSuccess(credential, recaptchaToken);
                })
                .catch((error: Error) => {
                    // Let Next.js control-flow errors (e.g. redirect from a server action) bubble up to the router.
                    unstable_rethrow(error);
                    onError(error);
                });
        },
        [recaptchaCallback, onSuccess, onError],
    );
}
